/**
 * RematchWaitingScreen.tsx
 * Rematch acceptance waiting screen with countdown
 */

import { type FC, useMemo } from 'react';

import {
  Button,
  Icon,
  TextBlock,
  Title,
  useAdaptive,
} from '@skillarea/lib-frontend';
import type { GameReturnDto } from '@skillarea/lib-shared';
import { useTranslation } from 'react-i18next';

import styles from '../MatchmakingModal.module.scss';
import { formatCountdownMs } from '../utils/formatCountdownMs';

import { useLocalizedGame } from '~/hooks/useLocalizedGame';

export interface IRematchWaitingScreenProps {
  game: GameReturnDto;
  rematchRequestId?: string;
  timeLeftMs: number;
  onCancel: () => void;
  isDeclining?: boolean;
}

export const RematchWaitingScreen: FC<IRematchWaitingScreenProps> = ({
  game,
  isDeclining = false,
  onCancel,
  rematchRequestId,
  timeLeftMs,
}) => {
  const { t } = useTranslation('matchmaking');
  const localizedGame = useLocalizedGame(game);
  const { isMobile } = useAdaptive();
  const timerText = useMemo(() => formatCountdownMs(timeLeftMs), [timeLeftMs]);
  const watchIconSize = isMobile ? 104 : 142;

  return (
    <div
      className={styles.screen}
      data-rematch-request-id={rematchRequestId}
      data-testid="rematch-waiting-screen"
    >
      <div className={styles.centered}>
        <Title
          className={styles.screenTitle}
          color="contrast"
          size="l"
          variant="benzin"
        >
          {localizedGame.name}
        </Title>

        <div className={styles.spinnerContainer}>
          <Icon img="watch" size={watchIconSize} type="colored" />
        </div>

        <TextBlock className={styles.searchTimer}>{timerText}</TextBlock>

        <TextBlock className={styles.searchingText}>
          {t('rematch.waiting')}
        </TextBlock>
      </div>

      <div className={styles.footer}>
        <div className={styles.actionButtons}>
          <Button
            className={styles.actionButton}
            color="primary"
            disabled={isDeclining}
            name={t('rematch.back')}
            onClick={onCancel}
            size="s"
            variant="filled"
          >
            {t('rematch.back')}
          </Button>
        </div>
      </div>
    </div>
  );
};
